import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface AdminOrg {
  id: string;
  name: string;
  email: string | null;
  subscription_status: string;
  trial_ends_at: string;
  created_at: string;
}

export interface AdminProfile {
  id: string;
  user_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  organisation_id: string | null;
}

export interface AdminAuthUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
}

export interface AdminReservation {
  id: string;
  organisation_id: string;
  date: string;
  status: string;
  price: number;
  created_at: string;
}

export interface AdminSubscription {
  id: string;
  organisation_id: string;
  status: string;
  amount: number;
  next_payment_date: string | null;
}

export interface AdminCustomer {
  id: string;
  organisation_id: string;
  email: string;
  created_at: string;
}

export interface AdminInvoice {
  id: string;
  organisation_id: string;
  amount_incl_tax: number;
  status: string;
  paid_at: string | null;
  period_start: string;
  period_end: string;
  created_at: string;
}

export interface AdminPaidBooking {
  id: string;
  reservation_id: string;
  created_at: string;
}

const emptyState = {
  organisations: [] as AdminOrg[],
  profiles: [] as AdminProfile[],
  authUsers: [] as AdminAuthUser[],
  reservations: [] as AdminReservation[],
  subscriptions: [] as AdminSubscription[],
  customers: [] as AdminCustomer[],
  invoices: [] as AdminInvoice[],
  paidBookings: [] as AdminPaidBooking[],
};

export const useAdminData = () => {
  const { session, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [organisations, setOrganisations] = useState<AdminOrg[]>([]);
  const [profiles, setProfiles] = useState<AdminProfile[]>([]);
  const [authUsers, setAuthUsers] = useState<AdminAuthUser[]>([]);
  const [reservations, setReservations] = useState<AdminReservation[]>([]);
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [customers, setCustomers] = useState<AdminCustomer[]>([]);
  const [invoices, setInvoices] = useState<AdminInvoice[]>([]);
  const [paidBookings, setPaidBookings] = useState<AdminPaidBooking[]>([]);

  const fetchData = useCallback(async () => {
    if (authLoading) return;

    if (!session) {
      setError("Unauthorized");
      Object.entries(emptyState).forEach(([, val]) => val);
      setOrganisations(emptyState.organisations);
      setProfiles(emptyState.profiles);
      setAuthUsers(emptyState.authUsers);
      setReservations(emptyState.reservations);
      setSubscriptions(emptyState.subscriptions);
      setCustomers(emptyState.customers);
      setInvoices(emptyState.invoices);
      setPaidBookings(emptyState.paidBookings);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("admin-get-data");
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      setOrganisations(data.organisations || []);
      setProfiles(data.profiles || []);
      setAuthUsers(data.authUsers || []);
      setReservations(data.reservations || []);
      setSubscriptions(data.subscriptions || []);
      setCustomers(data.customers || []);
      setInvoices(data.invoices || []);
      setPaidBookings(data.paidBookings || []);
    } catch (e: any) {
      setError(e.message || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, [authLoading, session]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { loading, error, organisations, profiles, authUsers, reservations, subscriptions, customers, invoices, paidBookings, refetch: fetchData };
};
